/* eslint-disable react/no-did-mount-set-state */
import React from 'react';
import { withRouter } from 'react-router-dom';
import {
  get, sortBy, filter, includes, isFinite,
} from 'lodash';
import moment from 'moment-timezone';
import {
  Paper, Button, Tooltip,
  Typography, List, ListItem,
} from '@mui/material';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import MUIDataTable from 'mui-datatables';

import api from '../../lib/api';
import marketActions from '../../actions/markets';
import statActions from '../../actions/stats';
import * as snackbarActions from '../../actions/snackbar';

import CustomToolbar from './custom-toolbar';
import EditMarketTable from './market-edit-markets';
import {
  getMatchingMarket,
  VEHICLE_MISSING,
  VEHICLE_MAINTENANCE,
  OpsBrandNames,
  findTargetDeploymentForDate,
  getMarketName,
} from '../../lib/utils';
import {
  VEHICLE_TYPE_URL_NAME,
  VEHICLE_STATUS_URL_NAME,
} from '../../lib/routes/routeHelper';
import { tableOptions } from '../../lib/table-utils';
import WrappedSpinner from '../common/wrapped-spinner';

const styles = {
  root: {
    width: '95%',
    marginLeft: 'auto',
    marginRight: 'auto',
    overflowX: 'auto',
  },
  floatRight: {
    float: 'right',
  },
  assetList: {
    display: 'flex',
  },
  brand: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
  },
  markets: {
    textAlign: 'left',
  },
  vehicles: {
    flex: 2,
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    alignContent: 'center',
    justifyContent: 'center',
  },
};

class MarketPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      editMarkets: false,
    };

    this.navigateToMarket = this.navigateToMarket.bind(this);
    this.navigateToEdit = this.navigateToEdit.bind(this);
    this.handleSaveMarkets = this.handleSaveMarkets.bind(this);
    this.handleCancelMarketEdit = this.handleCancelMarketEdit.bind(this);
  }

  componentDidMount() {
    const { fetchStats } = this.props;
    const userMarketPreferences = JSON.parse(localStorage.getItem('userMarketPreferences'));
    if (userMarketPreferences && userMarketPreferences.length > 0) {
      this.setState({ userMarketPreferences });
      fetchStats(userMarketPreferences);
    } else {
      this.setState({ editMarkets: true });
    }
  }

  get markets() {
    const markets = get(this, 'props.markets', []);
    const marketPreferences = get(this, 'state.userMarketPreferences');
    const matchingMarkets = filter(markets, (share) => includes(marketPreferences, share._id));

    return sortBy(
      matchingMarkets,
      (market) => getMarketName(market).toLowerCase(),
    );
  }

  get data() {
    const { stats } = this.props;
    return this.markets.map((market) => {
      const marketId = get(market, '_id', '');
      const marketName = getMarketName(market) || '';
      const tz = get(market, 'tz', 'America/New_York');
      const targetDeployments = get(stats, `targets.${marketId}`, []);
      const zagsterTarget = get(market, 'zagsterContractedVehicles', 0);
      const vehicleResultArray = get(stats, `vehicles.${marketId}`, []);

      const mbtTargets = sortBy(vehicleResultArray, (result) => `${result.brand}${result.type}`).map(({
        brand, type, actives, missing, maintenance,
      }) => {
        let marketTarget = zagsterTarget;
        if (brand !== OpsBrandNames.ZAGSTER) {
          // filter a market's targets for this brand/type, then find the correct date
          const targets = targetDeployments.filter(
            (target) => target.vehicleBrand === brand && target.vehicleType === type,
          );
          marketTarget = findTargetDeploymentForDate(tz, targets, moment.tz(tz)).target;
        }
        return {
          marketId, brand, type, target: marketTarget, active: actives, missing, maintenance,
        };
      });

      return [
        marketId,
        marketName,
        mbtTargets, // brands display
        mbtTargets, // types display
        mbtTargets, // active/target
        mbtTargets, // % deployed
        mbtTargets, // missing
        mbtTargets, // maintenance
      ];
    });
  }

  get mainView() {
    const { editMarkets, userMarketPreferences } = this.state;
    const { isLoading } = this.props;
    if (editMarkets) {
      return (
        <EditMarketTable
          marketPreferences={userMarketPreferences}
          handleSave={this.handleSaveMarkets}
          handleCancel={this.handleCancelMarketEdit}
        />
      );
    } else {
      return (
        <WrappedSpinner isLoading={isLoading}>
          <MUIDataTable
            title="Markets"
            columns={this.columns}
            data={this.data}
            options={this.tableOptions}
          />
        </WrappedSpinner>
      );
    }
  }

  get tableOptions() {
    const basicOptions = { ...tableOptions };

    basicOptions.rowsPerPage = 100;
    basicOptions.download = false;
    basicOptions.print = false;
    basicOptions.viewColumns = false;
    basicOptions.customToolbar = () => (
      <div style={styles.floatRight}>
        <CustomToolbar
          type="Wrench"
          onClick={this.navigateToEdit}
        />
      </div>
    );

    return basicOptions;
  }

  get columns() {
    const { history } = this.props;
    const columnData = [
      {
        name: 'ID',
        options: {
          display: 'excluded',
          filter: false,
        },
      },
      {
        name: 'Market',
        options: {
          filter: true,
          sort: false,
          customBodyRender: (value, tableMeta) => {
            const marketId = tableMeta.rowData[0];
            return (
              <Tooltip title="Visit Market Page">
                <Button
                  style={styles.markets}
                  color="primary"
                  onClick={() => this.navigateToMarket(marketId)}
                >
                  {value}
                </Button>
              </Tooltip>
            );
          },
        },
      },
      {
        name: 'Brand',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (mbtValues) => (
            <List style={styles.vehicles}>
              {mbtValues && sortBy(mbtValues, ({ brand, type }) => `${brand}${type}`).map(({ brand, type }) => (
                <ListItem key={`${brand}${type}`} dense disableGutters>
                  <Typography
                    align="center"
                    variant="subtitle1"
                    style={styles.brand}
                  >
                    {brand}
                  </Typography>
                </ListItem>
              ))}
            </List>
          ),
        },
      },
      {
        name: 'VehicleType',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (mbtValues) => (
            <List style={styles.vehicles}>
              {mbtValues && sortBy(mbtValues, ({ brand, type }) => `${brand}${type}`).map(({ brand, type }) => (
                <ListItem key={`${brand}${type}`} dense disableGutters>
                  <Typography
                    align="center"
                    variant="subtitle1"
                    style={styles.brand}
                  >
                    {type}
                  </Typography>
                </ListItem>
              ))}
            </List>
          ),
        },
      },
      {
        name: 'Active / Target',
        options: {
          filter: false,
          sort: false,
          hint: 'Active: Vehicle available for rent / Target: Vehicles contractually committed',
          customBodyRender: (mbtTargets) => (
            <List style={styles.vehicles}>
              {mbtTargets && sortBy(mbtTargets, ({ brand, type }) => `${brand}${type}`).map(({
                brand, type, active, target,
              }) => (
                <ListItem key={brand + type} dense disableGutters>
                  <Typography
                    align="center"
                    variant="subtitle1"
                    style={styles.brand}
                  >
                    {active}
                    /
                    {target}
                  </Typography>
                </ListItem>
              ))}
            </List>
          ),
        },
      },
      {
        name: '% Deployed',
        options: {
          filter: false,
          sort: false,
          hint: 'Active Vehicles / Target Vehicles (%)',
          customBodyRender: (mbtTargets) => (
            <List style={styles.vehicles}>
              {mbtTargets && sortBy(mbtTargets, ({ brand, type }) => `${brand}${type}`).map(({
                brand, type, active, target,
              }) => {
                const deployTargetPercent = (active / target);
                const deployTargetString = isFinite(deployTargetPercent) ? `${(deployTargetPercent * 100).toFixed(0)} %` : '';
                return (
                  <ListItem key={`${brand}${type}`} dense disableGutters>
                    <Typography
                      align="center"
                      variant="subtitle1"
                      style={styles.brand}
                    >
                      {deployTargetString}
                    </Typography>
                  </ListItem>
                );
              })}
            </List>
          ),
        },
      },
      {
        name: 'Missing Vehicles',
        options: {
          filter: false,
          sort: false,
          hint: 'Vehicles that have a status of Missing',
          customBodyRender: (mbtTargets) => (
            <List style={styles.vehicles}>
              {mbtTargets && sortBy(mbtTargets, ({ brand, type }) => `${brand}${type}`).map(({
                marketId, brand, type, missing,
              }) => (
                <ListItem key={`${brand}${type}`} dense disableGutters>
                  <Button color="primary" onClick={() => history.push(`/operator/markets/${marketId}/vehicles?${VEHICLE_TYPE_URL_NAME}=${type}&${VEHICLE_STATUS_URL_NAME}=${VEHICLE_MISSING}`)}>
                    <Typography
                      align="center"
                      variant="subtitle1"
                      style={styles.brand}
                    >
                      {missing}
                    </Typography>
                  </Button>
                </ListItem>
              ))}
            </List>
          ),
        },
      },
      {
        name: 'Maintenance Vehicles',
        options: {
          filter: false,
          sort: false,
          hint: 'Vehicles that have a status of Maintenance',
          customBodyRender: (mbtTargets) => (
            <List style={styles.vehicles}>
              {mbtTargets && sortBy(mbtTargets, ({ brand, type }) => `${brand}${type}`).map(({
                marketId, brand, type, maintenance,
              }) => (
                <ListItem key={`${brand}${type}`} dense disableGutters>
                  <Button color="primary" onClick={() => history.push(`/operator/markets/${marketId}/vehicles?${VEHICLE_TYPE_URL_NAME}=${type}&${VEHICLE_STATUS_URL_NAME}=${VEHICLE_MAINTENANCE}`)}>
                    <Typography
                      align="center"
                      variant="subtitle1"
                      style={styles.brand}
                    >
                      {maintenance}
                    </Typography>
                  </Button>
                </ListItem>
              ))}
            </List>
          ),
        },
      },
    ];
    return columnData;
  }

  navigateToMarket(marketId) {
    const markets = get(this, 'props.markets');
    const matchingMarket = getMatchingMarket(markets, marketId);
    const { history, selectMarket } = this.props;

    history.push(`/operator/markets/${marketId}`);
    selectMarket(matchingMarket);
  }

  navigateToEdit() {
    this.setState({ editMarkets: true });
  }

  handleSaveMarkets(shares) {
    const {
      auth,
      showErrorSnackbar,
    } = this.props;
    const userId = localStorage.getItem('userId');
    api.updateWrangler({ adminMarketIds: shares }, userId)
      .then(({ data: wrangler }) => {
        if (!wrangler) {
          auth.logout();
          return;
        }
        const userMarketPreferences = get(wrangler, 'adminMarketIds', []);
        localStorage.setItem('userMarketPreferences', JSON.stringify(userMarketPreferences));
        if (userMarketPreferences.length > 0) {
          this.setState({ editMarkets: false, userMarketPreferences });
        } else {
          this.setState({ editMarkets: true });
        }
      })
      .catch((error) => {
        showErrorSnackbar(`Error from server: ${error}`);
      });
  }

  handleCancelMarketEdit() {
    this.setState({ editMarkets: false });
  }

  render() {
    return (
      <Paper style={styles.root} sx={{ mt: 3 }}>
        {this.mainView}
      </Paper>
    );
  }
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...marketActions,
  ...statActions,
  ...snackbarActions,
}, dispatch);

const mapStateToProps = ({ markets, stats }) => ({
  markets: markets.markets,
  stats: stats.stats,
  isLoading: markets.isLoading || stats.isLoading,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(MarketPage));
